import { useCallback, useMemo, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import { Input } from "antd5";
import { Flex } from "styles/utility-components";

import { useDebouncedValue } from "lib/debounce";
import { PaginationOptions } from "lib/generated/app-service-gql/graphql";
import { useBids } from "lib/hooks/api/bid/bids/useBids";
import { usePersistedTableView } from "lib/hooks/usePersistedTableView";
import { useTablePageSize } from "lib/hooks/useTablePageSize";
import { parsePaginationUrlState, useURLState } from "lib/hooks/useURLState";
import { useIsLaptopUp } from "lib/providers/Responsive";
import EditColumnsButton from "../../lib/core_components/EditColumnsButton";
import { useTeams } from "../../lib/hooks/api/teams/useTeams";
import { EventDataTypes } from "../../lib/tracking";
import { ColumnSetting, TableSettings } from "../../lib/types/models";
import FilterDrawer from "../filter_form/filter_drawer/FilterDrawer";
import { TeamDropdownFilter } from "../form_components/dropdown_list_selects/TeamDropdownFilter";
import { UserDropdownFilter } from "../form_components/dropdown_list_selects/UserDropdownFilter";
import { clearBuyerFilters } from "../form_components/utils";
import Button from "../ui/button/Button";
import BidsActionBar from "./BidsActionBar";
import { BidsTable } from "./BidsTable";
import ExportBidsButton from "./ExportBidsButton";
import { FilterForm } from "./FilterForm";
import { QuickFilterBar } from "./QuickFilterBar";
import {
  ALL_BID_COLUMNS,
  BidColumns,
  BidFilters,
  BidFiltersSchema,
  BidSortState,
  convertBidFiltersToSearchRequest,
  DEFAULT_FILTERS,
  DEFAULT_VISIBLE_BID_COLUMNS,
  EMPTY_BID_FILTERS,
  mapSortFieldToEnum,
  mapSortOrderToEnum,
  SelectedBid,
} from "./utils";

type Props = {
  allAvailableColumns?: ColumnSetting<BidColumns>[];
  defaultVisibleColumns?: ColumnSetting<BidColumns>[];
  defaultFilters?: BidFilters;
};
export function FilterableBids({
  allAvailableColumns = ALL_BID_COLUMNS,
  defaultVisibleColumns = DEFAULT_VISIBLE_BID_COLUMNS,
  defaultFilters = DEFAULT_FILTERS,
}: Props) {
  const { data: teams } = useTeams();

  // Below 1280 the row cannot hold the labelled quick filters, so they give way to All
  // filters, which carries the same assignee and team fields.
  const showQuickFilters = useIsLaptopUp();

  const { appliedColumns, setColumns, storedSort, setSort } = usePersistedTableView<
    BidColumns,
    Pick<BidFilters, "orderBy" | "order">
  >("bidsTableView", allAvailableColumns, defaultVisibleColumns);

  const effectiveDefaultFilters = useMemo(
    () => (storedSort ? { ...defaultFilters, ...storedSort } : defaultFilters),
    [storedSort, defaultFilters],
  );

  const [filters = effectiveDefaultFilters, setBidRequestFilters] = useURLState(
    "bidFilters",
    effectiveDefaultFilters,
    BidFiltersSchema,
    { encoder: "compress" },
  );

  const [debouncedFilters] = useDebouncedValue(filters, 500);
  const [pageSize, setPageSize] = useTablePageSize("bids-table");
  const [pagination = { page: 1, pageSize }, setPagination] = useURLState<PaginationOptions>(
    "pagination",
    { page: 1, pageSize },
    parsePaginationUrlState,
  );
  const [debouncedPagination, updateDebouncedPagination] = useDebouncedValue(pagination, 500);
  const [selectedBids, setSelectedBids] = useState<SelectedBid[]>([]);

  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  const handleSelectionChange = (selectedIds: string[]) => {
    if (!data?.results) return;

    const selectedBidsWithStages = selectedIds.map((id) => {
      const bid = data.results.find((opp) => opp.id === id);
      return {
        id,
        stageId: bid?.stage.id || "",
        name: bid?.name || "",
      };
    });
    setSelectedBids(selectedBidsWithStages);
  };

  const setFilters = useCallback(
    (val: BidFilters) => {
      setBidRequestFilters(val);
      setPagination({ page: 1, pageSize });
    },
    [setBidRequestFilters, setPagination, pageSize],
  );

  const handleSortChange = useCallback(
    (newSort: BidSortState) => {
      const sort = {
        orderBy: mapSortFieldToEnum(newSort.field),
        order: mapSortOrderToEnum(newSort.order),
      };
      setFilters({ ...filters, ...sort });
      setSort(sort);
    },
    [filters, setFilters, setSort],
  );

  const { data, isLoading, isError } = useBids(
    convertBidFiltersToSearchRequest(debouncedFilters),
    debouncedPagination,
  );
  return (
    <Flex column gap={16}>
      <Flex gap={8} alignItems="center" flexWrap="wrap" aria-label="Bid action items">
        <Input
          placeholder="Search"
          prefix={<SearchOutlined />}
          value={filters?.searchText ?? ""}
          onChange={(e) => {
            setFilters({ ...filters, searchText: e.target.value });
          }}
          allowClear
          style={{ width: 350 }}
        />

        {/* The filter controls hold their width so the search input is what shrinks;
            without this they are squeezed and their labels truncate too early. */}
        <Flex gap={8} alignItems="center" flexShrink={0}>
          {showQuickFilters && (
            <>
              <UserDropdownFilter
                value={filters?.assignee ? filters.assignee : { ids: [], includeNulls: false }}
                onChange={(value) => setFilters({ ...filters, assignee: value })}
                options={{ noun: "assignee" }}
              />
              {teams && teams.length > 1 && (
                <TeamDropdownFilter
                  teamIds={filters.teamIds ?? []}
                  onChange={(teamIds) => setFilters({ ...filters, teamIds: teamIds })}
                />
              )}
            </>
          )}
          {/* Drops to the icon alone once the quick filters are gone, so the row still fits.
              The label is the accessible name either way. */}
          <Button
            as="button"
            buttonType="secondary"
            onClick={() => setIsFilterDrawerOpen(true)}
            icon="filterFunnel02"
            iconProps={{ color: "sysPrimaryDefault" }}
            aria-label="All filters"
          >
            {showQuickFilters ? "All filters" : undefined}
          </Button>
        </Flex>
      </Flex>
      <Flex gap={8} alignItems="center" flexWrap="wrap">
        <QuickFilterBar
          filters={filters}
          isLoading={isLoading}
          totalResults={data?.total ?? 0}
          valueSum={data?.valueSum ?? 0}
          onClearFilter={(key) => setFilters({ ...filters, ...clearBuyerFilters(key) })}
          onFilterClick={() => setIsFilterDrawerOpen(true)}
          clearAllFilters={() =>
            setFilters({
              ...EMPTY_BID_FILTERS,
              searchText: filters.searchText === "" ? undefined : filters.searchText,
            })
          }
        />
        {/* Holds its width and stays right-aligned, whether it shares the line with the
            filter summary or wraps onto its own below it. */}
        <RowActions gap={8} flexShrink={0}>
          <EditColumnsButton<TableSettings<BidColumns>>
            allAvailableColumns={[...allAvailableColumns]}
            selectedColumns={appliedColumns}
            onNewColumnSettingChange={(c) => setColumns([...c])}
            dataType={EventDataTypes.bids}
          />

          <ExportBidsButton
            filters={debouncedFilters}
            selectedBids={selectedBids.map((b) => b.id)}
            totalCount={data?.total ?? 0}
            isLoading={isLoading}
          />
        </RowActions>
      </Flex>
      {selectedBids.length > 0 && (
        <BidsActionBar
          selectedBidIds={selectedBids.map((b) => b.id)}
          onClearSelection={() => setSelectedBids([])}
        />
      )}
      <BidsTable
        bids={data?.results}
        total={data?.total ?? 0}
        sort={{
          field: filters.orderBy,
          order: filters.order,
        }}
        columnSettings={appliedColumns.map((c) => c.field)}
        isLoading={isLoading}
        isError={isError}
        selectedRowKeys={selectedBids.map((b) => b.id)}
        onSelectionChange={handleSelectionChange}
        onSortChange={handleSortChange}
        paginationState={{
          current: pagination.page ?? 1,
          pageSize: pagination.pageSize ?? pageSize,
        }}
        onPaginationStateChange={({ current, pageSize }) => {
          setPageSize(pageSize);
          setPagination({ page: current, pageSize });
          updateDebouncedPagination({ page: current, pageSize });
        }}
      />
      <FilterDrawer open={isFilterDrawerOpen} onClose={() => setIsFilterDrawerOpen(false)}>
        {isFilterDrawerOpen && (
          <FilterForm
            onClose={() => setIsFilterDrawerOpen(false)}
            onChange={setFilters}
            filters={filters ?? EMPTY_BID_FILTERS}
            hiddenFilters={teams && teams.length > 1 ? [] : ["team"]}
          />
        )}
      </FilterDrawer>
    </Flex>
  );
}

// margin-left rather than the row's justify-content, so the actions stay right-aligned
// even once they have wrapped onto a line of their own.
const RowActions = styled(Flex)({
  marginLeft: "auto",
});
